import { useId } from 'react';

import { CheckoutTextField } from 'components/CheckoutTextField';
import { CheckoutSelectField } from 'components/CheckoutFormControls';
import { RegistrationDateField } from 'components/RegistrationDateField';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Button } from 'components/ui/button';
import { Trans, t } from '@lingui/macro';
import { cn } from 'lib/utils';

export default function RegistrationField(props) {
    switch (props.type) {
        case 1:
            return <ShortAnswer {...props} />;
        case 2:
            return <Paragraph {...props} />;
        case 3:
            return <SingleChoice {...props} />;
        case 4:
            return <MultipleChoice {...props} />;
        case 5:
            return <Dropdown {...props} />;
        case 6:
            return <FullDate {...props} />;
        case 7:
            return <Age {...props} />;
        case 8:
            return <YesOrNo {...props} />;
        case 9:
            return <ShortDate {...props} />;
        case 10:
            return <TermsAndConditions {...props} />;
        case 999:
            return <TextBlock {...props} />;
        case 998:
            return <ImageBlock {...props} />;
        default:
            return null;
    }
}

function ShortAnswer(props) {
    const questionId = useId();

    return (
        <Question>
            <QuestionHeader id={questionId} required={Boolean(props.required)} title={props.desc} />
            <QuestionContent>
                <CheckoutTextField
                    value={props.value}
                    fullWidth
                    required={Boolean(props.required)}
                    aria-labelledby={questionId}
                    error={Boolean(props.errorMsg)}
                    helperText={props.errorMsg}
                    helperTextRole={props.errorMsg ? 'alert' : undefined}
                    onChange={(e) => props.onChange(props.index, e.target.value)}
                />
            </QuestionContent>
        </Question>
    );
}

function Paragraph(props) {
    const questionId = useId();

    return (
        <Question>
            <QuestionHeader id={questionId} required={Boolean(props.required)} title={props.desc} />
            <QuestionContent>
                <CheckoutTextField
                    value={props.value}
                    fullWidth
                    required={Boolean(props.required)}
                    aria-labelledby={questionId}
                    multiline
                    minRows={6}
                    maxRows={6}
                    error={Boolean(props.errorMsg)}
                    helperText={props.errorMsg}
                    helperTextRole={props.errorMsg ? 'alert' : undefined}
                    onChange={(e) => props.onChange(props.index, e.target.value)}
                />
            </QuestionContent>
        </Question>
    );
}

function SingleChoice(props) {
    const questionId = useId();
    const errorId = props.errorMsg ? `${questionId}-error` : undefined;

    return (
        <Question>
            <QuestionHeader
                id={questionId}
                required={Boolean(props.required)}
                title={props.desc}
                subheader={t`Pick one`}
            />
            <QuestionContent>
                <RadioGroup
                    value={props.value ? String(props.value) : undefined}
                    onValueChange={(nextValue) => props.onChange(props.index, Number(nextValue))}
                    aria-labelledby={questionId}
                    aria-describedby={errorId}
                    aria-required={Boolean(props.required)}
                    className="space-y-2"
                >
                    {props.options.map((o) => {
                        const optionId = `${questionId}-option-${o.id}`;

                        return (
                            <div key={o.id} className="flex items-center gap-2">
                                <RadioGroupItem value={String(o.id)} id={optionId} />
                                <Label htmlFor={optionId} className="font-normal">
                                    {o.desc}
                                </Label>
                            </div>
                        );
                    })}
                </RadioGroup>
                <RegFormHelperText id={errorId} error={Boolean(props.errorMsg)}>
                    {props.errorMsg}
                </RegFormHelperText>
            </QuestionContent>
        </Question>
    );
}

function MultipleChoice(props) {
    const questionId = useId();
    const errorId = props.errorMsg ? `${questionId}-error` : undefined;

    return (
        <Question>
            <QuestionHeader
                id={questionId}
                required={Boolean(props.required)}
                title={props.desc}
                subheader={t`Select all that apply`}
            />
            <QuestionContent>
                <div
                    role="group"
                    aria-labelledby={questionId}
                    aria-describedby={errorId}
                    className="space-y-2"
                >
                    {props.options.map((o) => {
                        const optionId = `${questionId}-option-${o.id}`;

                        return (
                            <div key={o.id} className="flex items-center gap-2">
                                <Checkbox
                                    id={optionId}
                                    checked={props.value.has(o.id)}
                                    onCheckedChange={(checked) =>
                                        props.onChange(props.index, checked === true, o.id)
                                    }
                                />
                                <Label htmlFor={optionId} className="font-normal">
                                    {o.desc}
                                </Label>
                            </div>
                        );
                    })}
                </div>
                <RegFormHelperText id={errorId} error={Boolean(props.errorMsg)}>
                    {props.errorMsg}
                </RegFormHelperText>
            </QuestionContent>
        </Question>
    );
}

function Dropdown(props) {
    const questionId = useId();
    const selectId = `${questionId}-select`;

    return (
        <Question>
            <QuestionHeader
                id={questionId}
                required={Boolean(props.required)}
                title={props.desc}
                subheader={t`Pick one`}
            />
            <QuestionContent>
                <CheckoutSelectField
                    id={selectId}
                    value={props.value}
                    error={Boolean(props.errorMsg)}
                    message={props.errorMsg}
                    onChange={(e) => props.onChange(props.index, Number(e.target.value))}
                >
                    <option disabled value=""></option>
                    {props.options.map((o) => (
                        <option key={o.id} value={o.id}>
                            {o.desc}
                        </option>
                    ))}
                </CheckoutSelectField>
            </QuestionContent>
        </Question>
    );
}

function FullDate(props) {
    const questionId = useId();
    const errorId = props.errorMsg ? `${questionId}-error` : undefined;

    return (
        <Question>
            <QuestionHeader id={questionId} required={Boolean(props.required)} title={props.desc} />
            <QuestionContent>
                <RegistrationDateField
                    value={props.value}
                    mode="full"
                    error={Boolean(props.errorMsg)}
                    errorId={errorId}
                    questionId={questionId}
                    required={Boolean(props.required)}
                    placeholder={t`YYYY-MM-DD`}
                    onChange={(date) => props.onChange(props.index, date)}
                />
                <RegFormHelperText id={errorId} error={Boolean(props.errorMsg)}>
                    {props.errorMsg}
                </RegFormHelperText>
            </QuestionContent>
        </Question>
    );
}

function ShortDate(props) {
    const questionId = useId();
    const errorId = props.errorMsg ? `${questionId}-error` : undefined;

    return (
        <Question>
            <QuestionHeader id={questionId} required={Boolean(props.required)} title={props.desc} />
            <QuestionContent>
                <RegistrationDateField
                    value={props.value}
                    mode="short"
                    error={Boolean(props.errorMsg)}
                    errorId={errorId}
                    questionId={questionId}
                    required={Boolean(props.required)}
                    placeholder={t`YYYY-MM`}
                    onChange={(date) => props.onChange(props.index, date)}
                />
                <RegFormHelperText id={errorId} error={Boolean(props.errorMsg)}>
                    {props.errorMsg}
                </RegFormHelperText>
            </QuestionContent>
        </Question>
    );
}

function Age(props) {
    const questionId = useId();

    return (
        <Question>
            <QuestionHeader id={questionId} required={Boolean(props.required)} title={props.desc} />
            <QuestionContent>
                <CheckoutTextField
                    value={props.value}
                    type="tel"
                    required={Boolean(props.required)}
                    aria-labelledby={questionId}
                    error={Boolean(props.errorMsg)}
                    helperText={props.errorMsg ?? undefined}
                    helperTextRole={props.errorMsg ? 'alert' : undefined}
                    onChange={(e) => props.onChange(props.index, e.target.value)}
                />
            </QuestionContent>
        </Question>
    );
}

function YesOrNo(props) {
    const questionId = useId();
    const errorId = props.errorMsg ? `${questionId}-error` : undefined;

    return (
        <Question>
            <QuestionHeader id={questionId} required={Boolean(props.required)} title={props.desc} />
            <QuestionContent>
                <div
                    role="group"
                    className="inline-flex rounded-md border border-solid border-border"
                    aria-labelledby={questionId}
                    aria-describedby={errorId}
                >
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => props.onChange(props.index, true)}
                        aria-pressed={props.value === true}
                        className={cn(
                            'h-9 w-[100px] rounded-none rounded-l-md border-0 border-r border-solid border-border bg-transparent text-sm text-muted-foreground hover:bg-transparent',
                            props.value === true &&
                                'bg-primary/15 text-primary hover:bg-primary/15',
                        )}
                    >
                        <Trans>Yes</Trans>
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => props.onChange(props.index, false)}
                        aria-pressed={props.value === false}
                        className={cn(
                            'h-9 w-[100px] rounded-none rounded-r-md border-0 bg-transparent text-sm text-muted-foreground hover:bg-transparent',
                            props.value === false &&
                                'bg-primary/15 text-primary hover:bg-primary/15',
                        )}
                    >
                        <Trans>No</Trans>
                    </Button>
                </div>
                <RegFormHelperText id={errorId} error={Boolean(props.errorMsg)}>
                    {props.errorMsg}
                </RegFormHelperText>
            </QuestionContent>
        </Question>
    );
}

function TermsAndConditions(props) {
    const questionId = useId();
    const errorId = props.errorMsg ? `${questionId}-error` : undefined;
    const checkboxId = `${questionId}-terms`;

    return (
        <Question>
            <QuestionHeader id={questionId} required={Boolean(props.required)} title={props.desc} />
            <QuestionContent>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id={checkboxId}
                        checked={props.value}
                        required={Boolean(props.required)}
                        aria-labelledby={questionId}
                        aria-describedby={errorId}
                        onCheckedChange={(checked) => props.onChange(props.index, checked === true)}
                    />
                    <Label htmlFor={checkboxId} className="font-normal">
                        {t`I agree`}
                    </Label>
                </div>
                <RegFormHelperText id={errorId} error={Boolean(props.errorMsg)}>
                    {props.errorMsg}
                </RegFormHelperText>
            </QuestionContent>
        </Question>
    );
}

function TextBlock(props) {
    // Extra top margin if the text block is not first on the form
    // Helpful for grouping form into sections
    return (
        <div className={cn('mb-10', props.index === 0 ? 'mt-10' : 'mt-16')}>
            <p className="whitespace-pre-wrap font-bold text-foreground">{props.desc}</p>
        </div>
    );
}

function ImageBlock(props) {
    return (
        <div className="mx-auto my-8 w-full max-w-[520px] rounded-xl">
            {props.imageLink ? (
                <a
                    href={props.imageLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                >
                    <img className="w-full rounded-xl object-contain" src={props.imageUrl} alt="" />
                </a>
            ) : (
                <img className="w-full rounded-xl object-contain" src={props.imageUrl} alt="" />
            )}
        </div>
    );
}

function QuestionHeader(props) {
    return (
        <div>
            <div className="flex">
                <span id={props.id} className="inline whitespace-pre-wrap text-foreground">
                    {props.title}
                </span>
                {props.required && (
                    <span className="ml-1 inline text-xl font-bold text-destructive">*</span>
                )}
            </div>
            {props.subheader && (
                <span className="block text-xs text-muted-foreground">
                    <Trans>{props.subheader}</Trans>
                </span>
            )}
        </div>
    );
}

function RegFormHelperText({ children, error, id }) {
    if (!children) return null;

    return (
        <p
            id={id}
            role={error ? 'alert' : undefined}
            className={cn('mt-1 text-sm text-muted-foreground', error && 'text-destructive')}
        >
            {children}
        </p>
    );
}

function Question({ children }) {
    return <div className="mb-8">{children}</div>;
}

function QuestionContent({ children }) {
    return <div className="mt-4">{children}</div>;
}
